import { useParams, useNavigate } from "@tanstack/react-router"
import { trpc } from "~/lib/trpc"
import { Tabs, TabsContent, TabsList, TabsTrigger, Button } from "@aix/ui"
import { Building2, ArrowLeft, Settings, Loader2, ExternalLink, Plus, Archive, RotateCcw } from "lucide-react"
import { useState, useRef, useEffect } from "react"
import { ConnectAccountModal } from "../MetaAdsScreen/components/ConnectAccountModal"
import toast from "react-hot-toast"

export function CompanyDetailScreen() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("overview")
  const [showMetaConnectModal, setShowMetaConnectModal] = useState(false)
  const [showSettingsMenu, setShowSettingsMenu] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)

  const { data: company, isLoading, refetch } = trpc.companies.getBySlug.useQuery({ slug })

  const archiveMutation = trpc.companies.delete.useMutation({
    onSuccess: () => {
      toast.success("Company archived")
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to archive company")
    },
  })

  const unarchiveMutation = trpc.companies.update.useMutation({
    onSuccess: () => {
      toast.success("Company restored")
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to restore company")
    },
  })

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Fetch Meta Ad accounts for this company
  const {
    data: metaAccountsData,
    isLoading: metaAccountsLoading,
    refetch: refetchMetaAccounts,
  } = trpc.metaAds.listConnectedAccounts.useQuery({ companyId: company?.id }, { enabled: !!company?.id })

  const metaAccounts = metaAccountsData?.accounts || []

  // Fetch EAS Projects for this company
  const { data: allEasProjects, isLoading: easProjectsLoading } = trpc.easUpdates.listProjects.useQuery()

  const easProjects = allEasProjects?.filter((project) => project.companyId === company?.id) || []

  // Fetch SEO Products for this company
  const {
    data: seoProductsData,
    isLoading: seoProductsLoading,
    refetch: refetchSeoProducts,
  } = trpc.companies.listSeoProducts.useQuery({ companyId: company?.id }, { enabled: !!company?.id })

  const seoProducts = seoProductsData?.products || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!company) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-4">
        <Building2 className="w-16 h-16 text-muted-foreground" />
        <h2 className="text-xl sm:text-2xl font-semibold">Company not found</h2>
        <Button onClick={() => navigate({ to: "/companies" })}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Companies
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <Button variant="ghost" onClick={() => navigate({ to: "/companies" })} className="mb-4 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
            {company.color ? (
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex-shrink-0 shadow-md"
                style={{ backgroundColor: company.color }}
              />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground truncate">{company.name}</h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">{company.description}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs sm:text-sm font-medium">
                  {company.slug}
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs sm:text-sm font-mono">
                  {company.infisicalPath}
                </span>
                {company.status === "archived" && (
                  <span className="px-3 py-1 rounded-full bg-destructive/10 text-destructive text-xs sm:text-sm font-medium">
                    Archived
                  </span>
                )}
              </div>
            </div>

            <div className="relative sm:ml-auto flex-shrink-0" ref={settingsRef}>
              <Button variant="outline" onClick={() => setShowSettingsMenu(!showSettingsMenu)}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>

              {showSettingsMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-background border border-border rounded-lg shadow-lg z-10">
                  {company.status === "archived" ? (
                    <button
                      onClick={() => {
                        unarchiveMutation.mutate({ id: company.id, status: "active" })
                        setShowSettingsMenu(false)
                      }}
                      disabled={unarchiveMutation.isLoading}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-muted transition-colors rounded-lg"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Restore Company
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (confirm("Are you sure you want to archive this company?")) {
                          archiveMutation.mutate({ id: company.id })
                          setShowSettingsMenu(false)
                        }
                      }}
                      disabled={archiveMutation.isLoading}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-destructive hover:bg-destructive/10 transition-colors rounded-lg"
                    >
                      <Archive className="w-4 h-4" />
                      Archive Company
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-2 mb-6">
            <TabsList className="inline-flex min-w-full sm:min-w-0">
              <TabsTrigger value="overview" className="flex-1 sm:flex-none">
                Overview
              </TabsTrigger>
              <TabsTrigger value="seo" className="flex-1 sm:flex-none">
                SEO Products
              </TabsTrigger>
              <TabsTrigger value="eas" className="flex-1 sm:flex-none">
                EAS Projects
              </TabsTrigger>
              <TabsTrigger value="meta-ads" className="flex-1 sm:flex-none">
                Meta Ads
              </TabsTrigger>
              <TabsTrigger value="crm" className="flex-1 sm:flex-none">
                CRM Accounts
              </TabsTrigger>
              <TabsTrigger value="services" className="flex-1 sm:flex-none">
                Services
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* SEO Products Card */}
              <div
                className="p-4 sm:p-6 border border-border rounded-lg bg-card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setActiveTab("seo")}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-muted-foreground">SEO Products</h3>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-foreground">
                  {seoProductsLoading ? "..." : seoProducts.length}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">Active products</p>
              </div>

              {/* EAS Projects Card */}
              <div
                className="p-4 sm:p-6 border border-border rounded-lg bg-card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setActiveTab("eas")}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-muted-foreground">EAS Projects</h3>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-foreground">
                  {easProjectsLoading ? "..." : easProjects.length}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">Active projects</p>
              </div>

              {/* Meta Ads Card */}
              <div
                className="p-4 sm:p-6 border border-border rounded-lg bg-card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setActiveTab("meta-ads")}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-muted-foreground">Meta Ad Accounts</h3>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-foreground">
                  {metaAccountsLoading ? "..." : metaAccounts.length}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">Connected accounts</p>
              </div>

              {/* Services Card */}
              <div className="p-4 sm:p-6 border border-border rounded-lg bg-card hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-muted-foreground">Total Services</h3>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-foreground">0</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">All integrations</p>
              </div>
            </div>

            {/* Company Details */}
            <div className="mt-6 sm:mt-8 p-4 sm:p-6 border border-border rounded-lg bg-card">
              <h3 className="text-lg font-semibold mb-4">Company Details</h3>
              <dl className="space-y-3 sm:space-y-4">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 pb-3 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">ID</dt>
                  <dd className="text-sm font-mono text-foreground break-all">{company.id}</dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 pb-3 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
                  <dd className="text-sm font-mono text-foreground">{company.slug}</dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 pb-3 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Infisical Path</dt>
                  <dd className="text-sm font-mono text-foreground">{company.infisicalPath}</dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 pb-3 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                  <dd className="text-sm text-foreground capitalize">{company.status}</dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                  <dd className="text-sm text-foreground">{new Date(company.createdAt).toLocaleDateString()}</dd>
                </div>
              </dl>
            </div>
          </TabsContent>

          <TabsContent value="seo" className="mt-0">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold">SEO Products</h3>
                <p className="text-sm text-muted-foreground">SEO configurations and domains for {company.name}</p>
              </div>
              <Button disabled>
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </div>

            {seoProductsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : seoProducts.length === 0 ? (
              <div className="text-center py-12 sm:py-16 border border-border rounded-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🔍</span>
                </div>
                <h4 className="text-lg font-semibold mb-2">No SEO Products</h4>
                <p className="text-sm text-muted-foreground mb-6">
                  Add your first SEO product to manage SEO content for {company.name}
                </p>
                <Button disabled>
                  <Plus className="w-4 h-4 mr-2" />
                  Add SEO Product
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {seoProducts.map((product) => (
                  <div
                    key={product.id}
                    className="p-4 sm:p-6 border border-border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{product.name}</h4>
                        <p className="text-sm text-muted-foreground font-mono">{product.key}</p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Domain:</span>
                        <a
                          href={`https://${product.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {product.domain}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Hubs:</span>
                        <span className="font-mono">{Object.keys(product.hubs ?? {}).length}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-border flex gap-2">
                      <span className="text-xs text-muted-foreground">
                        Created {new Date(product.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="eas" className="mt-0">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold">EAS Projects</h3>
                <p className="text-sm text-muted-foreground">Expo Application Services projects for {company.name}</p>
              </div>
              <Button onClick={() => navigate({ to: "/eas-dashboard" })}>
                <ExternalLink className="w-4 h-4 mr-2" />
                View EAS Dashboard
              </Button>
            </div>

            {easProjectsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : easProjects.length === 0 ? (
              <div className="text-center py-12 sm:py-16 border border-border rounded-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">📱</span>
                </div>
                <h4 className="text-lg font-semibold mb-2">No EAS Projects</h4>
                <p className="text-sm text-muted-foreground mb-6">
                  Add your first EAS project to manage mobile app deployments
                </p>
                <Button onClick={() => navigate({ to: "/eas-dashboard" })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add EAS Project
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {easProjects.map((project) => (
                  <div
                    key={project.id}
                    className="p-4 sm:p-6 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate({ to: `/eas-dashboard/${project.slug}` })}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      {project.icon && <span className="text-3xl">{project.icon}</span>}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground truncate">{project.name}</h4>
                        <p className="text-sm text-muted-foreground font-mono">@{project.slug}</p>
                      </div>
                      {project.isActive ? (
                        <div className="w-2 h-2 bg-green-500 rounded-full" title="Active" />
                      ) : (
                        <div className="w-2 h-2 bg-gray-400 rounded-full" title="Inactive" />
                      )}
                    </div>

                    {project.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{project.description}</p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.platforms.map((platform) => (
                        <span
                          key={platform}
                          className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded"
                        >
                          {platform === "ios" ? "📱 iOS" : "🤖 Android"}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {project.channels.slice(0, 3).map((channel) => (
                        <span key={channel} className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">
                          {channel}
                        </span>
                      ))}
                      {project.channels.length > 3 && (
                        <span className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">
                          +{project.channels.length - 3} more
                        </span>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-border">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate({ to: `/eas-dashboard/${project.slug}` })
                        }}
                        className="text-sm text-primary hover:underline"
                      >
                        View Project →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="meta-ads" className="mt-0">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold">Meta Ad Accounts</h3>
                <p className="text-sm text-muted-foreground">Manage Meta Ads accounts for {company.name}</p>
              </div>
              <Button onClick={() => setShowMetaConnectModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </div>

            {metaAccountsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : metaAccounts.length === 0 ? (
              <div className="text-center py-12 sm:py-16 border border-border rounded-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold mb-2">No Meta Ad Accounts</h4>
                <p className="text-sm text-muted-foreground mb-6">
                  Connect your first Meta Ads account to start managing campaigns
                </p>
                <Button onClick={() => setShowMetaConnectModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Meta Account
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {metaAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="p-4 border border-border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{account.accountName}</h4>
                        <p className="text-sm text-muted-foreground font-mono">{account.accountId}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-muted-foreground">Active</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {account.isPrimary && (
                        <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                          Primary
                        </span>
                      )}
                      {account.lastSyncAt && (
                        <span className="text-xs text-muted-foreground">
                          Last sync: {new Date(account.lastSyncAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-border">
                      <button
                        onClick={() =>
                          navigate({
                            to: "/meta-ads",
                            // @ts-ignore
                            search: { accountId: account.accountId },
                          })
                        }
                        className="text-sm text-primary hover:underline"
                      >
                        View Campaigns →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showMetaConnectModal && (
              <ConnectAccountModal
                onClose={() => setShowMetaConnectModal(false)}
                onSuccess={() => {
                  setShowMetaConnectModal(false)
                  void refetchMetaAccounts()
                }}
                preselectedCompanyId={company.id}
              />
            )}
          </TabsContent>

          <TabsContent value="crm" className="mt-0">
            <div className="text-center py-12 sm:py-16 border border-border rounded-lg">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">📧</span>
              </div>
              <h4 className="text-lg font-semibold mb-2">CRM Accounts Coming Soon</h4>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                Manage email, SMS, and platform accounts for {company.name}.
                <br />
                Email accounts (hello@pebble.com), Twilio SMS, and more.
              </p>
              <div className="text-xs text-muted-foreground bg-secondary px-4 py-2 rounded-md inline-block">
                Schema migrated ✅ | UI coming in next update
              </div>
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-0">
            <div className="text-center py-12 sm:py-16">
              <p className="text-sm sm:text-base text-muted-foreground">
                All services and integrations will be displayed here
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
